import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDownIcon, ChevronUpIcon, EllipsisIcon, PlusIcon, RotateCcwIcon, SparklesIcon, Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SettingsNav, SettingsNavItem } from "@/components/ui/settings-layout";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EvePipelinePhase } from "@/types/eve";
import { evePhaseLabel, isCustomPhaseKey } from "@/lib/eve";
import { AddPhaseDialog } from "./AddPhaseDialog";
import { phaseIcon, phaseNoun } from "./shared";

/** One block of phases in the nav: a tier's pipeline, or the one every tier shares. */
export interface PipelineNavGroup {
  /** The tier this group edits; null = every tier (shared pipeline / single-tier campaign). */
  tier: number | null;
  /** Group heading ("Tier 2", "All 3 tiers"); omitted on single-tier campaigns (no heading at all). */
  title?: string;
  /** Muted text after the title — the tier's offer. */
  subtitle?: string;
  /** The group's phases, sorted by order. */
  pipeline: EvePipelinePhase[];
  /** Tiers a save in this group targets: [tier] per tier, null = every tier. */
  targetTiers: number[] | null;
  /** The heading's ⋯ menu. */
  menu?: ReactNode;
}

interface PipelineNavProps {
  groups: PipelineNavGroup[];
  /** "overview" or a phase key, and the tier it was opened in (null = shared / single-tier). */
  selected: { section: string; tier: number | null };
  /** Resolves a section slug (+ the tier it belongs to) to a NavLink target. */
  linkTo: (slug: string, tier: number | null) => string;
  onReorder: (group: PipelineNavGroup, key: string, direction: "up" | "down") => void;
  /** Reset to defaults / Delete picked from a phase's ⋯ menu (the caller confirms and writes). */
  onPhaseAction: (group: PipelineNavGroup, phase: EvePipelinePhase, kind: "reset" | "delete") => void;
  reordering: boolean;
  /** Navigate to a freshly created phase's editor. */
  onCreated: (key: string, tier: number | null) => void;
}

// A phase item's ⋯ menu shows on hover / keyboard focus / while open (desktop):
// the label makes room for it and the badges step aside.
const MENU_ROOM = "md:group-hover/nav:pr-8 md:group-has-focus-visible/nav:pr-8 md:group-has-data-[state=open]/nav:pr-8";
const HIDE_FOR_MENU =
  "md:group-hover/nav:opacity-0 md:group-has-focus-visible/nav:opacity-0 md:group-has-data-[state=open]/nav:opacity-0";

const HOVER_REVEAL =
  "md:opacity-0 md:group-hover/tier:opacity-100 md:focus-within:opacity-100 md:has-data-[state=open]:opacity-100";

/**
 * SettingsTab-literal left nav for the pipeline editor: Overview, then the
 * phases — each with a hover ⋯ menu on desktop (Move up / Move down, then
 * Reset to defaults / Delete).
 * Multi-tier campaigns list every tier's phases under a "Tier N · offer"
 * heading (or one "All N tiers" heading while they share a pipeline), each
 * heading carrying its own add button and ⋯ menu.
 */
export function PipelineNav({
  groups,
  selected,
  linkTo,
  onReorder,
  onPhaseAction,
  reordering,
  onCreated,
}: PipelineNavProps) {
  const grouped = groups.some((g) => g.title);
  const single = groups[0];

  const addPhase = (group: PipelineNavGroup, trigger: ReactNode) => (
    <AddPhaseDialog
      pipeline={group.pipeline}
      targetTiers={group.targetTiers}
      onCreated={(key) => onCreated(key, group.tier)}
      trigger={trigger}
    />
  );

  const phaseItem = (group: PipelineNavGroup, phase: EvePipelinePhase, i: number) => {
    const Icon = phaseIcon(phase);
    const active = selected.section === phase.key && selected.tier === group.tier;
    const enabled = phase.enabled === true;
    const label = evePhaseLabel(phase, group.pipeline);
    const custom = isCustomPhaseKey(phase.key);
    return (
      <div key={phase.key} data-context-menu className="group/nav relative w-full">
        <SettingsNavItem asChild active={active} className={cn(MENU_ROOM, !enabled && "opacity-60")}>
          <NavLink to={linkTo(phase.key, group.tier)}>
            <Icon />
            <span className="truncate">{label}</span>
            {(custom || !enabled) && (
              // Far right; the ⋯ menu takes their spot while it shows.
              <span className={`ml-auto flex shrink-0 items-center gap-1 ${HIDE_FOR_MENU}`}>
                {custom && <Badge variant="info" size="sm">custom</Badge>}
                {!enabled && <Badge variant="muted" size="sm">off</Badge>}
              </span>
            )}
          </NavLink>
        </SettingsNavItem>
        <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden md:flex items-center opacity-0 group-hover/nav:opacity-100 group-has-focus-visible/nav:opacity-100 has-data-[state=open]:opacity-100 transition-none">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="muted-ghost"
                size="icon-xs"
               
                aria-label={`${label} options`}
              >
                <EllipsisIcon className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuItem
                disabled={i === 0 || reordering}
                onSelect={() => onReorder(group, phase.key, "up")}
              >
                <ChevronUpIcon />
                Move up
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={i === group.pipeline.length - 1 || reordering}
                onSelect={() => onReorder(group, phase.key, "down")}
              >
                <ChevronDownIcon />
                Move down
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={reordering} onSelect={() => onPhaseAction(group, phase, "reset")}>
                <RotateCcwIcon />
                Reset to defaults
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={reordering}
                onSelect={() => onPhaseAction(group, phase, "delete")}
              >
                <Trash2Icon />
                Delete {phaseNoun(phase.key).toLowerCase()}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  return (
    <SettingsNav
      title="Pipeline"
      action={
        !grouped &&
        addPhase(
          single,
          <Button variant="muted-ghost" size="icon-sm" title="Add Phase">
            <PlusIcon className="size-3.5" />
          </Button>
        )
      }
    >
      <SettingsNavItem asChild active={selected.section === "overview"}>
        <NavLink to={linkTo("overview", selected.tier)}>
          <SparklesIcon />
          Overview
        </NavLink>
      </SettingsNavItem>

      {grouped
        ? groups.map((group) => (
            // `contents` on mobile: the headings and phases flow inline in the horizontal nav.
            <div key={group.tier ?? "all"} className="group/tier contents md:flex md:flex-col md:gap-1 md:mt-4">
              <div
                data-context-menu
                className="flex h-7 shrink-0 items-center gap-1.5 pl-2 pr-0.5 text-xs md:pl-2.5 max-md:ml-2 max-md:border-l"
              >
                <span className="shrink-0 font-medium text-foreground/80">{group.title}</span>
                {group.subtitle && (
                  <span className="min-w-0 truncate text-muted-foreground max-md:hidden">{group.subtitle}</span>
                )}
                <div className={`ml-auto flex shrink-0 items-center ${HOVER_REVEAL}`}>
                  {addPhase(
                    group,
                    <Button
                      variant="muted-ghost"
                      size="icon-xs"
                     
                      aria-label={`Add phase to ${group.title}`}
                      title="Add Phase"
                    >
                      <PlusIcon className="size-3.5" />
                    </Button>
                  )}
                  {group.menu}
                </div>
              </div>
              {/* The tier's phases, indented under its heading on a dashed rail. */}
              <div className="contents md:ml-3 md:flex md:flex-col md:gap-1 md:border-l md:border-dashed md:pl-1.5">
                {group.pipeline.map((phase, i) => phaseItem(group, phase, i))}
                {group.pipeline.length === 0 && (
                  <p className="hidden md:block px-2 py-1 text-xs text-muted-foreground/70">No phases yet</p>
                )}
              </div>
            </div>
          ))
        : single.pipeline.map((phase, i) => phaseItem(single, phase, i))}

      {/* Mobile add button (the horizontal nav has no header row; grouped headings carry their own). */}
      {!grouped &&
        addPhase(
          single,
          <Button variant="ghost" size="sm" className="md:hidden flex shrink-0">
            <PlusIcon className="size-4" />
            Add
          </Button>
        )}
    </SettingsNav>
  );
}
